import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Flex, Row, Skeleton, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@nks/shared';
import { EChart, type EChartClick, type EChartOption } from '../../components/EChart';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/format';
import { formatMoney, isPositive } from '../../lib/money';
import { useHasPermission } from '../auth/session';
import {
  REVENUE_STREAMS,
  fetchSalesByGroup,
  fetchSalesSummary,
  type SalesGroup,
  type SalesLinesFilter,
  type SalesSummary,
  type Stream,
} from './api';
import { LAST_YEAR_SERIES, salesTooltipFormatter } from './chart-tooltip';
import { changeRatio, formatPercent, monthName, plotValue, streamTotal } from './figures';
import { SalesLinesDrawer } from './SalesLinesDrawer';
import { StreamMapDrawer } from './StreamMapDrawer';
import { Tile } from './Tile';

const { Text } = Typography;

/**
 * Stack colours, by stream and never by rank, so a stream keeps its colour in
 * every year. Unclassified is deliberately grey: it is the stream nobody chose.
 */
export const STREAM_COLOUR: Record<Stream, string> = {
  BIKES: 'var(--series-1)',
  PARTS: 'var(--series-2)',
  LABOUR: 'var(--series-3)',
  FEES: 'var(--series-4)',
  UNCLASSIFIED: 'var(--ink-subtle)',
  COLLECTED: 'var(--ink-subtle)',
  FINANCE: 'var(--ink-subtle)',
};

/** A vehicle line is Bikes whatever its group says, so its row is not the group's to move. */
const isVehicleRow = (g: SalesGroup): boolean => g.stream === 'BIKES' && !g.mapped;

const groupKey = (g: SalesGroup): string => `${g.itemGroup ?? ''}|${g.stream}|${String(g.mapped)}`;

interface Drill {
  filter: SalesLinesFilter;
  title: string;
}

function useChartOption(summary: SalesSummary | undefined, language: string, lastYearLabel: string): EChartOption {
  const { t } = useTranslation();
  return useMemo(() => {
    if (summary === undefined) return {};
    const compact = new Intl.NumberFormat(language, { notation: 'compact', maximumFractionDigits: 1 });
    const axisText = { color: 'var(--ink-muted)' };
    return {
      grid: { left: 8, right: 8, top: 16, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: salesTooltipFormatter(summary, (month) => `${monthName(month, language, 'long')} ${String(summary.year)}`),
        backgroundColor: 'var(--surface-raised)',
        borderColor: 'var(--line)',
        textStyle: { color: 'var(--ink)' },
        confine: true,
      },
      xAxis: {
        type: 'category',
        data: summary.months.map((m) => monthName(m.month, language)),
        axisLabel: axisText,
        axisLine: { lineStyle: { color: 'var(--line-strong)' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { ...axisText, formatter: (v: number) => compact.format(v) },
        splitLine: { lineStyle: { color: 'var(--line)' } },
      },
      series: [
        ...REVENUE_STREAMS.map((stream) => ({
          id: stream,
          name: t(`insights.stream.${stream}`),
          type: 'bar',
          stack: 'revenue',
          barMaxWidth: 32,
          cursor: 'pointer',
          // A surface-coloured edge keeps neighbouring segments apart.
          itemStyle: { color: STREAM_COLOUR[stream], borderColor: 'var(--surface)', borderWidth: 1 },
          emphasis: { focus: 'series' },
          data: summary.months.map((m) => plotValue(m.byStream[stream].revenue)),
        })),
        {
          id: LAST_YEAR_SERIES,
          name: lastYearLabel,
          type: 'line',
          symbolSize: 8,
          lineStyle: { color: 'var(--ink-muted)', width: 2, type: 'dashed' },
          itemStyle: { color: 'var(--ink-muted)', borderColor: 'var(--surface)', borderWidth: 2 },
          data: summary.months.map((m) => plotValue(m.lastYearRevenue)),
        },
      ],
    };
  }, [summary, language, lastYearLabel, t]);
}

/** Insights, Sales: AutoCount's sales by month and stream, beside last year. */
export function SalesTab({ year }: { year: number }): ReactNode {
  const { t, i18n } = useTranslation();
  const canConfigure = useHasPermission(PERMISSIONS.ANALYTICS_CONFIGURE);
  const [drill, setDrill] = useState<Drill | null>(null);
  const [mapping, setMapping] = useState<(SalesGroup & { itemGroup: string }) | null>(null);
  const [onlyUnclassified, setOnlyUnclassified] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  const summary = useQuery({ queryKey: ['analytics', 'sales', 'summary', year], queryFn: () => fetchSalesSummary(year) });
  const groups = useQuery({ queryKey: ['analytics', 'sales', 'by-group', year], queryFn: () => fetchSalesByGroup(year) });

  const language = i18n.language;
  // The year the figures are for, as the API says: the labels follow the data.
  const lastYear = (summary.data?.year ?? year) - 1;
  const lastYearLabel = t('insights.sales.lastYearSeries', { year: String(lastYear) });
  const option = useChartOption(summary.data, language, lastYearLabel);

  const error = summary.error ?? groups.error;
  if (error !== null) {
    const denied = error instanceof ApiError && error.status === 403;
    return <Alert type="error" showIcon message={t(denied ? 'insights.noAccess' : 'insights.loadFailed')} />;
  }
  if (summary.data === undefined) return <Skeleton active paragraph={{ rows: 8 }} />;

  const data = summary.data;
  if (data.dataAsOf === null) {
    return <Alert type="info" showIcon message={t('insights.noSales')} />;
  }

  const streamLabel = (s: Stream): string => t(`insights.stream.${s}`);
  const { totals } = data;
  const through = totals.comparedThroughMonth;
  const range =
    through === 1
      ? t('insights.sales.vsMonth', { from: monthName(1, language), year: String(lastYear) })
      : t('insights.sales.vsRange', {
          from: monthName(1, language),
          to: monthName(through, language),
          year: String(lastYear),
        });
  const change = changeRatio(totals.revenue, totals.lastYearRevenue);
  const collected = streamTotal(data.months, 'COLLECTED');
  const unclassified = streamTotal(data.months, 'UNCLASSIFIED');

  const openMonth = (click: EChartClick): void => {
    const stream = REVENUE_STREAMS.find((s) => s === click.seriesId);
    const m = data.months[click.dataIndex];
    if (stream === undefined || m === undefined) return;
    setDrill({
      filter: { year, month: m.month, stream },
      title: t('insights.lines.titleMonth', {
        stream: streamLabel(stream),
        month: monthName(m.month, language, 'long'),
        year: String(year),
      }),
    });
  };

  const openGroup = (g: SalesGroup): void => {
    const name = g.itemGroup ?? t('insights.sales.noItemGroup');
    setDrill({
      // The stream as well as the group: a group's vehicle lines and its other
      // lines are separate rows, and each opens only its own.
      filter: { year, itemGroup: g.itemGroup ?? '', stream: g.stream },
      title: t('insights.lines.titleGroup', { group: `${name} · ${streamLabel(g.stream)}`, year: String(year) }),
    });
  };

  const showUnclassified = (): void => {
    setOnlyUnclassified(true);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const rows = (groups.data ?? []).filter((g) => !onlyUnclassified || g.stream === 'UNCLASSIFIED');

  const columns: TableProps<SalesGroup>['columns'] = [
    {
      title: t('insights.sales.itemGroup'),
      key: 'group',
      render: (_: unknown, g) =>
        g.itemGroup === null ? (
          <Text type="secondary">{t('insights.sales.noItemGroup')}</Text>
        ) : (
          <Text className="tabular">{g.itemGroup}</Text>
        ),
    },
    {
      title: t('insights.sales.stream'),
      key: 'stream',
      render: (_: unknown, g) => (
        <Flex gap={6} align="center" wrap>
          <span>
            {isVehicleRow(g) ? t('insights.sales.vehicle', { stream: streamLabel(g.stream) }) : streamLabel(g.stream)}
          </span>
          {g.mapped && <Tag>{t('insights.sales.chosen')}</Tag>}
        </Flex>
      ),
    },
    {
      title: t('insights.sales.amount'),
      key: 'revenue',
      align: 'right',
      render: (_: unknown, g) => (
        <Button
          type="link"
          className="tabular"
          style={{ padding: 0 }}
          onClick={() => {
            openGroup(g);
          }}
        >
          {formatMoney(g.revenue)}
        </Button>
      ),
    },
    { title: t('insights.sales.lines'), key: 'lines', align: 'right', render: (_: unknown, g) => g.lines },
    ...(canConfigure
      ? [
          {
            title: '',
            key: 'action',
            render: (_: unknown, g: SalesGroup) => {
              const { itemGroup } = g;
              if (itemGroup === null || isVehicleRow(g)) return null;
              return (
                <Button
                  size="small"
                  onClick={() => {
                    setMapping({ ...g, itemGroup });
                  }}
                >
                  {t('insights.sales.changeStream')}
                </Button>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <Flex vertical gap={16}>
      <Text type="secondary">{t('insights.dataAsOf', { at: formatDateTime(data.dataAsOf) })}</Text>

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} xl={6}>
          <Tile id="revenue" label={t('insights.sales.revenue')} value={formatMoney(totals.revenue)}>
            <Text type="secondary">
              {change === null
                ? t('insights.sales.vsLastYearNone', { range })
                : t('insights.sales.vsLastYear', {
                    range,
                    amount: formatMoney(totals.lastYearRevenue),
                    change: formatPercent(change, language, true),
                  })}
            </Text>
          </Tile>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Tile
            id="profit"
            label={t('insights.sales.grossProfit')}
            value={formatMoney(totals.grossProfit)}
          >
            <Text type="secondary">
              {t('insights.sales.coverage', { percent: formatPercent(Number(totals.costCoverage), language) })}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('insights.sales.returnsNote')}
            </Text>
          </Tile>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Tile id="collected" label={t('insights.sales.collected')} value={formatMoney(collected)}>
            <Text type="secondary">{t('insights.sales.collectedHelp')}</Text>
          </Tile>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Tile id="unclassified" label={t('insights.sales.unclassified')} value={formatMoney(unclassified)}>
            {isPositive(unclassified) ? (
              <>
                <Text type="secondary">{t('insights.sales.unclassifiedHelp')}</Text>
                {canConfigure && (
                  <Button type="link" style={{ padding: 0, alignSelf: 'flex-start' }} onClick={showUnclassified}>
                    {t('insights.sales.chooseStreams')}
                  </Button>
                )}
              </>
            ) : (
              <Text type="secondary">{t('insights.sales.unclassifiedNone')}</Text>
            )}
          </Tile>
        </Col>
      </Row>

      <Card size="small" title={t('insights.sales.chartTitle')}>
        <Flex vertical gap={8}>
          <Text type="secondary">{t('insights.sales.chartHelp')}</Text>
          {/* The legend is HTML rather than ECharts' own: on a phone, a canvas
           * legend either scrolls a page at a time or runs into the axis. */}
          <Flex gap={12} wrap component="ul" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {REVENUE_STREAMS.map((stream) => (
              <Flex key={stream} component="li" gap={6} align="center">
                <span
                  aria-hidden
                  style={{ width: 12, height: 12, borderRadius: 3, background: STREAM_COLOUR[stream] }}
                />
                <Text>{streamLabel(stream)}</Text>
              </Flex>
            ))}
            <Flex component="li" gap={6} align="center">
              <span aria-hidden style={{ width: 16, borderTop: '2px dashed var(--ink-muted)' }} />
              <Text>{lastYearLabel}</Text>
            </Flex>
          </Flex>
          <EChart option={option} height={340} onClick={openMonth} />
        </Flex>
      </Card>

      <div ref={tableRef} data-testid="by-group">
        <Card
          size="small"
          title={t('insights.sales.byGroupTitle')}
          extra={
            onlyUnclassified ? (
              <Button
                size="small"
                onClick={() => {
                  setOnlyUnclassified(false);
                }}
              >
                {t('insights.sales.showAll')}
              </Button>
            ) : null
          }
        >
          <Flex vertical gap={8}>
            <Text type="secondary">{t('insights.sales.byGroupHelp')}</Text>
            <Table<SalesGroup>
              rowKey={groupKey}
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
              loading={groups.isPending}
              locale={{ emptyText: t('insights.sales.emptyGroups') }}
              dataSource={rows}
              columns={columns}
            />
          </Flex>
        </Card>
      </div>

      <SalesLinesDrawer
        filter={drill?.filter ?? null}
        title={drill?.title ?? ''}
        onClose={() => {
          setDrill(null);
        }}
      />
      <StreamMapDrawer
        group={mapping}
        year={year}
        onClose={() => {
          setMapping(null);
        }}
      />
    </Flex>
  );
}
